import { createSlice } from "@reduxjs/toolkit";

import {
  fetchDrivers,
  fetchRoutesNomenclatures,
  fetchIntranodesNomenclatures,
  fetchUniqueDrivers,
  fetchActiveTransports,
  fetchTransportHistory,
} from "../api/transport.api";
import {
  ActiveTransportsInterface,
  AssignedDriverDataInterface,
  AssignedDriversInterface,
  IntranodNomenclatureDataInterface,
  IntranodesNomenclaturesInterface,
  RouteNomenclatureDataInterface,
  RoutesNomenclaturesInterface,
  UniqueDriversInterface,
} from "../interfaces/transport.interface";

export interface TransportInterface {
  assignedDriversData: AssignedDriversInterface;
  assignedDriverData: AssignedDriverDataInterface | null;
  routesNomenclatures: RoutesNomenclaturesInterface;
  routeNomenclatureData: RouteNomenclatureDataInterface | null;
  intranodesNomenclatures: IntranodesNomenclaturesInterface;
  intranodNomenclatureData: IntranodNomenclatureDataInterface | null;
  uniqueDrivers: UniqueDriversInterface;
  activeTransports: ActiveTransportsInterface;
  transportHistory: ActiveTransportsInterface;
}

export const defaultStateTransport: TransportInterface = {
  assignedDriversData: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  assignedDriverData: null,
  routesNomenclatures: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  routeNomenclatureData: null,
  intranodesNomenclatures: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  intranodNomenclatureData: null,
  uniqueDrivers: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  activeTransports: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  transportHistory: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
};

const transport = createSlice({
  name: "transport",
  initialState: defaultStateTransport,
  reducers: {
    setAssignDriver: (
      state,
      action: {
        payload: AssignedDriverDataInterface | null;
      },
    ) => {
      return {
        ...state,
        assignedDriverData: action.payload,
      };
    },
    setRoutesNomenclatures: (
      state,
      action: {
        payload: RouteNomenclatureDataInterface;
      },
    ) => {
      return {
        ...state,
        routeNomenclatureData: action.payload,
      };
    },
    setIntranodesNomenclatures: (
      state,
      action: {
        payload: IntranodNomenclatureDataInterface;
      },
    ) => {
      return {
        ...state,
        intranodNomenclatureData: action.payload,
      };
    },
    clearAssignedDriversData: (state) => {
      return {
        ...state,
        assignedDriversData: defaultStateTransport.assignedDriversData,
      };
    },
    clearTransportHistoryData: (state) => {
      return {
        ...state,
        transportHistory: defaultStateTransport.transportHistory,
      };
    },
  },
  extraReducers: (builder) => {
    // assign start
    builder.addCase(fetchDrivers.pending, (state) => {
      return {
        ...state,
        assignedDriversData: {
          ...state.assignedDriversData,
          isLoading: true,
          isError: false,
          errorMessage: "",
        },
      };
    });
    builder.addCase(fetchDrivers.rejected, (state, action: any) => {
      return {
        ...state,
        assignedDriversData: {
          ...state.assignedDriversData,
          isLoading: false,
          isError: true,
          errorMessage: action.payload.message,
        },
      };
    });
    builder.addCase(fetchDrivers.fulfilled, (state, action) => {
      return {
        ...state,
        assignedDriversData: {
          ...state.assignedDriversData,
          data: action.payload,
          isLoading: false,
          isError: false,
          errorMessage: "",
        },
      };
    });
    // assign end

    // assign nomroute start
    builder.addCase(fetchRoutesNomenclatures.pending, (state) => {
      return {
        ...state,
        routesNomenclatures: {
          ...state.routesNomenclatures,
          isLoading: true,
          isError: false,
          errorMessage: "",
        },
      };
    });
    builder.addCase(fetchRoutesNomenclatures.rejected, (state, action: any) => {
      return {
        ...state,
        routesNomenclatures: {
          ...state.routesNomenclatures,
          isLoading: false,
          isError: true,
          errorMessage: action.payload.message,
        },
      };
    });
    builder.addCase(fetchRoutesNomenclatures.fulfilled, (state, action) => {
      return {
        ...state,
        routesNomenclatures: {
          ...state.routesNomenclatures,
          data: action.payload,
          isLoading: false,
          isError: false,
          errorMessage: "",
        },
      };
    });
    // assign nomroute end

    // assign nomrouteintranod start
    builder.addCase(fetchIntranodesNomenclatures.pending, (state) => {
      return {
        ...state,
        intranodesNomenclatures: {
          ...state.intranodesNomenclatures,
          isLoading: true,
          isError: false,
          errorMessage: "",
        },
      };
    });
    builder.addCase(
      fetchIntranodesNomenclatures.rejected,
      (state, action: any) => {
        return {
          ...state,
          intranodesNomenclatures: {
            ...state.intranodesNomenclatures,
            isLoading: false,
            isError: true,
            errorMessage: action.payload.message,
          },
        };
      },
    );
    builder.addCase(fetchIntranodesNomenclatures.fulfilled, (state, action) => {
      return {
        ...state,
        intranodesNomenclatures: {
          ...state.intranodesNomenclatures,
          data: action.payload,
          isLoading: false,
          isError: false,
          errorMessage: "",
        },
      };
    });
    // assign nomrouteintranod end
    // assign unique drivers interface start
    builder.addCase(fetchUniqueDrivers.pending, (state) => {
      return {
        ...state,
        uniqueDrivers: {
          ...state.uniqueDrivers,
          isLoading: true,
          isError: false,
          errorMessage: "",
        },
      };
    });
    builder.addCase(fetchUniqueDrivers.rejected, (state, action: any) => {
      return {
        ...state,
        uniqueDrivers: {
          ...state.uniqueDrivers,
          isLoading: false,
          isError: true,
          errorMessage: action.payload.message,
        },
      };
    });
    builder.addCase(fetchUniqueDrivers.fulfilled, (state, action) => {
      return {
        ...state,
        uniqueDrivers: {
          ...state.uniqueDrivers,
          data: action.payload,
          isLoading: false,
          isError: false,
          errorMessage: "",
        },
      };
    });
    // assign unique drivers interface end

    // fetch active transports start
    builder.addCase(fetchActiveTransports.pending, (state) => {
      return {
        ...state,
        activeTransports: {
          ...defaultStateTransport.activeTransports,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchActiveTransports.rejected, (state, action: any) => {
      return {
        ...state,
        activeTransports: {
          ...state.activeTransports,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchActiveTransports.fulfilled, (state, action) => {
      return {
        ...state,
        activeTransports: {
          ...state.activeTransports,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // fetch active transports end
    // fetch transport history start
    builder.addCase(fetchTransportHistory.pending, (state) => {
      return {
        ...state,
        transportHistory: {
          ...defaultStateTransport.transportHistory,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchTransportHistory.rejected, (state, action: any) => {
      return {
        ...state,
        transportHistory: {
          ...state.transportHistory,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchTransportHistory.fulfilled, (state, action) => {
      return {
        ...state,
        transportHistory: {
          ...state.transportHistory,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // fetch transport history end
  },
});

export const {
  setAssignDriver,
  setRoutesNomenclatures,
  setIntranodesNomenclatures,
  clearAssignedDriversData,
  clearTransportHistoryData,
} = transport.actions;

export default transport.reducer;
